import * as Dialog from '@radix-ui/react-dialog';
import { useRef, type ReactNode } from 'react';

import { Button } from '@/components/Button/Button';

import styles from './QuestionActionDialog.module.css';

interface QuestionActionDialogProps {
  confirmLabel: string;
  destructive?: boolean;
  // A fixed message; the server's text is never shown.
  errorMessage: string | null;
  isPending: boolean;
  onConfirm(): void;
  onOpenChange(open: boolean): void;
  // Called as the dialog closes, to put focus back. The dialog is opened from a
  // menu item, so there is no Dialog.Trigger for Radix to return focus to.
  onRestoreFocus(): void;
  open: boolean;
  pendingLabel: string;
  // What the change will not do: shown quietly under the summary.
  reassurance: ReactNode;
  // What the change will do, with real numbers.
  summary: ReactNode;
  title: string;
}

/**
 * Asks before a change to the whole class. The repo's confirmation shape, with
 * the alertdialog role. Focus starts on Cancel, the safe choice. While the
 * change runs the dialog cannot be dismissed, so its result is always seen.
 */
export function QuestionActionDialog({
  confirmLabel,
  destructive = false,
  errorMessage,
  isPending,
  onConfirm,
  onOpenChange,
  onRestoreFocus,
  open,
  pendingLabel,
  reassurance,
  summary,
  title,
}: QuestionActionDialogProps) {
  const cancelRef = useRef<HTMLElement>(null);

  return (
    <Dialog.Root
      onOpenChange={(nextOpen) => {
        if (!isPending) {
          onOpenChange(nextOpen);
        }
      }}
      open={open}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          className={styles.content}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            onRestoreFocus();
          }}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            cancelRef.current?.focus();
          }}
          role="alertdialog"
        >
          <div className={styles.text}>
            <Dialog.Title className={styles.title}>{title}</Dialog.Title>
            <Dialog.Description asChild>
              <div className={styles.description}>
                <p className={styles.summary}>{summary}</p>
                <p className={styles.reassurance}>{reassurance}</p>
              </div>
            </Dialog.Description>
          </div>

          {errorMessage ? (
            <p className={styles.error} role="alert">
              {errorMessage}
            </p>
          ) : null}

          <div className={styles.footer}>
            <Dialog.Close asChild>
              <Button disabled={isPending} ref={cancelRef} variant="secondary">
                Cancel
              </Button>
            </Dialog.Close>
            <Button destructive={destructive} disabled={isPending} onClick={onConfirm}>
              {isPending ? pendingLabel : confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
